// PRD 14 I5 (ADR-0020 § Amendment I0 — the register of every migrated family, Q12 widened) — Tenis10 Claims: one row per
// legacy family (the tenis10.ro account) with the state of its children and of its migration invitation, a search (a
// family, a parent, a child, an id, the start of an email — P29 — or the parent's whole phone number — P77), the filters on
// the states and the counts per state above the list.
// The state of the list lives in the address (shareable, back-button safe). The I0 simulation's cp-overview.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, withRouter } from "react-router-dom"
import { Card, CardBody, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import SearchBox from "components/Common/SearchBox"
import { getCounters, getFamilies } from "./api"
import { REGISTER_COUNTERS, REGISTER_FILTERS, childrenBadges, errorText, formatDateTime, invitationBadges, isRegisterFilter, legacyIdText } from "./claimsLabels"
import LegacyId, { LegacySearchTip } from "./LegacyId"
import "../../assets/scss/custom/pages/_tenis10Claims.scss"

export const REGISTER_PATH = "/accounts/tenis10-claims"
const LIMIT = 20

/** The list's state from the address: `{ state, q, page }`. */
export const readRegisterQuery = search => {
  const params = new URLSearchParams(search)
  const page = Number(params.get("page"))
  const state = params.get("state")
  return {
    state: isRegisterFilter(state) ? state : "all",
    q: (params.get("q") || "").slice(0, 100),
    page: Number.isInteger(page) && page > 0 && page <= 10000 ? page : 1,
  }
}
export const writeRegisterQuery = ({ state, q, page }) => {
  const params = new URLSearchParams()
  if (state && state !== "all") params.set("state", state)
  if (q) params.set("q", q)
  if (page > 1) params.set("page", String(page))
  return params.toString() ? `?${params}` : ""
}

// RELEASE-TENIS10 § 11 P78: what the search reads (the Accounts family register) — a child's number finds its family too;
// P103: a plain number finds the Sportya numbers only, a tenis10.ro id is written with its prefix
export const SEARCH_HINT = "Search the start of a name, a #ID (a Sportya account or a child's number), a tenis10.ro ID written T10#33245 (a family or a child), the start of an email (3 characters at least) or a whole phone number."

const Badges = ({ items }) => items.map(([tone, text]) => <span key={text} className={`badge badge-soft-${tone} mr-1 mb-1`}>{text}</span>)

export function Tenis10Claims({ history, location }) {
  const query = useMemo(() => readRegisterQuery(location.search), [location.search])
  const [searchText, setSearchText] = useState(query.q)
  const [list, setList] = useState({ status: "loading" })
  const [counters, setCounters] = useState({ status: "loading" })
  const timer = useRef()
  // the latest list request: an older answer (a Retry, a filter changed meanwhile) never replaces a newer one
  const request = useRef(0)
  const update = useCallback(next => {
    const search = writeRegisterQuery(next)
    if (search !== history.location.search) history.replace({ ...history.location, search })
  }, [history])

  const loadList = useCallback(() => {
    const id = ++request.current
    setList({ status: "loading" })
    getFamilies({ ...query, limit: LIMIT })
      .then(data => request.current === id && setList({ status: "ready", data }))
      .catch(error => request.current === id && setList({ status: "error", message: errorText(error, "The families didn't load. Click Retry.") }))
  }, [query])
  const loadCounters = useCallback(() => {
    setCounters({ status: "loading" })
    getCounters()
      .then(data => setCounters({ status: "ready", data }))
      .catch(error => setCounters({ status: "error", message: errorText(error, "The counters didn't load.") }))
  }, [])
  useEffect(() => {
    loadList()
    return () => { request.current += 1 }
  }, [loadList])
  useEffect(loadCounters, [loadCounters])
  useEffect(() => { setSearchText(query.q) }, [query.q])
  useEffect(() => () => clearTimeout(timer.current), [])

  const changeSearch = value => {
    setSearchText(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => update({ ...readRegisterQuery(history.location.search), q: value.trim(), page: 1 }), 400)
  }
  // the „×”: the whole register at once, without the typing delay
  const clearSearch = () => {
    clearTimeout(timer.current)
    setSearchText("")
    update({ ...readRegisterQuery(history.location.search), q: "", page: 1 })
  }
  const choose = state => { clearTimeout(timer.current); update({ ...query, q: searchText.trim(), state, page: 1 }) }
  // P103 — the help line's search: the tenis10.ro id, at once
  const searchFor = q => { clearTimeout(timer.current); setSearchText(q); update({ ...query, q, page: 1 }) }
  const rows = list.data?.families || []
  const total = list.data?.total ?? 0
  const pages = Math.max(1, list.data?.totalPages || 1)
  const first = rows.length ? (query.page - 1) * LIMIT + 1 : 0

  return <div className="page-content tenis10-claims"><Container fluid>
    <Breadcrumbs title="User Accounts" breadcrumbItem="Tenis10 Claims" path={REGISTER_PATH} />
    <Card><CardBody>
      <div className="d-flex flex-wrap justify-content-between align-items-baseline">
        <h4 className="card-title mb-1">Every migrated family</h4>
        <Link to={`${REGISTER_PATH}/log`} className="font-size-13"><i className="bx bx-history mr-1" aria-hidden="true" />Activity log</Link>
      </div>
      <p className="card-title-desc mb-3">Each tenis10.ro account with children, its Tenis10 migration invitation and the state of every child. A parent's “Nu vreau acest profil” hides the child at once and shows up here; only an admin can show it again.</p>
      {counters.status === "error" ? <p role="alert" className="text-danger mb-0">{counters.message} <button type="button" className="btn btn-link btn-sm p-0" onClick={loadCounters}>Retry</button></p>
        : <div className="tenis10-claims-counters" aria-busy={counters.status === "loading"}>
          {REGISTER_COUNTERS.map(([label, read, tone]) => <div key={label} className="tenis10-claims-counter">
            <div className="text-muted font-size-12">{label}</div>
            <div className="tenis10-claims-counter-value">{counters.status === "ready" ? read(counters.data).toLocaleString("ro-RO") : "…"}</div>
            <div className={`tenis10-claims-counter-bar badge-soft-${tone}`} />
          </div>)}
        </div>}
    </CardBody></Card>

    <Card><CardBody className="p-0">
      <div className="p-3 border-bottom">
        <SearchBox look="pill" className="tenis10-claims-search" aria-label="Search a family, a parent, a child, an ID, an email or a phone"
          value={searchText} maxLength={100} onValueChange={changeSearch} onClear={clearSearch}
          placeholder="Search a family, a parent, a child, an ID, an email or a phone" aria-describedby="tenis10-claims-search-hint" />
        <div id="tenis10-claims-search-hint" className="text-muted font-size-12 mt-1">{SEARCH_HINT}</div>
        <LegacySearchTip search={query.q} onSearch={searchFor} className="mt-1" />
        <div className="tenis10-claims-chips mt-2" role="group" aria-label="Filter the families">
          {REGISTER_FILTERS.map(([key, label]) => <button key={key} type="button" aria-pressed={query.state === key}
            className={`tenis10-claims-chip${query.state === key ? " active" : ""}`} onClick={() => choose(key)}>{label}</button>)}
        </div>
      </div>
      {list.status === "error" ? <div className="tenis10-claims-state" role="alert"><p>{list.message}</p>
        <button type="button" className="btn btn-primary" onClick={loadList}>Retry</button></div>
        : list.status === "loading" ? <div className="tenis10-claims-state" role="status"><Spinner size="sm" className="mr-2" />Loading the families…</div>
          : !rows.length ? <div className="tenis10-claims-state" role="status"><p className="mb-0">No family matches the search and the filter.</p></div>
            : <div className="table-responsive"><table className="table table-hover tenis10-claims-table" aria-label="Migrated families">
              <thead className="thead-light"><tr>
                <th scope="col">Legacy family</th><th scope="col">Children</th><th scope="col">Migration invitation</th>
                <th scope="col">Account</th><th scope="col">Last activity</th><th scope="col"><span className="sr-only">Open</span></th>
              </tr></thead>
              <tbody>{rows.map(row => <tr key={row.invitationId}>
                <td><div className="font-weight-medium"><LegacyId id={row.legacyUserId} /> · {[row.parent.firstName, row.parent.lastName].filter(Boolean).join(" ") || "—"}</div>
                  <div className="text-muted font-size-12">{row.children.total} {row.children.total === 1 ? "child" : "children"}</div></td>
                <td data-label="Children"><Badges items={childrenBadges(row.children)} /></td>
                <td data-label="Migration invitation"><Badges items={invitationBadges(row)} /></td>
                <td data-label="Account">{row.boundTo ? `#${row.boundTo.userId}` : "—"}</td>
                <td data-label="Last activity">{formatDateTime(row.lastActivityAt)}</td>
                <td data-label=""><Link className="btn btn-outline-primary btn-sm" to={{ pathname: `${REGISTER_PATH}/families/${row.invitationId}`, state: { registerSearch: location.search } }}
                  aria-label={`Open family ${legacyIdText(row.legacyUserId)}`}>Open</Link></td>
              </tr>)}</tbody>
            </table></div>}
      {list.status === "ready" && rows.length > 0 && <div className="tenis10-claims-footer">
        <div role="status">Showing <b>{first}–{first + rows.length - 1}</b> of <b>{total.toLocaleString("ro-RO")}</b> families</div>
        <nav aria-label="Family pages" className="tenis10-claims-pager">
          <button type="button" aria-label="Previous page" className="btn btn-light btn-sm" disabled={query.page <= 1}
            onClick={() => update({ ...query, page: query.page - 1 })}><i className="bx bx-chevron-left" aria-hidden="true" /></button>
          <span>Page {query.page} of {pages}</span>
          <button type="button" aria-label="Next page" className="btn btn-light btn-sm" disabled={query.page >= pages}
            onClick={() => update({ ...query, page: query.page + 1 })}><i className="bx bx-chevron-right" aria-hidden="true" /></button>
        </nav>
      </div>}
    </CardBody></Card>
  </Container></div>
}

export default withRouter(Tenis10Claims)
